// src/components/departments/EditStaffForm.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStaff } from '../../contexts/StaffContext'; // ARCHIVE: replaces direct Firestore access

function EditStaffForm({ staffMember, onStaffUpdated, onCancel }) {
  const { t } = useTranslation('staff');
  const { updateStaffMember, updateStaffEmploymentInfo } = useStaff(); // ARCHIVE
  const [activeTab, setActiveTab] = useState('basic'); // 'basic', 'employment', or 'vacation'

  // Form state
  const [staffName, setStaffName] = useState(staffMember.name || '');
  // ARCHIVE: pin state removed entirely — was always empty for any hashed
  // staff member (staffMember.pin only exists on legacy plaintext docs),
  // and its "Save" path wrote that empty/plaintext value straight back to
  // Firestore uncredited. PIN changes now happen only via Staff Management /
  // Staff Detail, which hash correctly and run the duplicate check.
  const [email, setEmail] = useState(staffMember.email || '');
  const [role, setRole] = useState(staffMember.role || 'staff');

  // Employment details
  const startDate = staffMember.employmentInfo?.startDate
    ? new Date(staffMember.employmentInfo.startDate.seconds * 1000)
    : new Date();
  const [startDateStr, setStartDateStr] = useState(startDate.toISOString().split('T')[0]);

  // Vacation days
  const currentYear = new Date().getFullYear().toString();
  const [vacationDays, setVacationDays] = useState(
    String(staffMember.employmentInfo?.vacationDays?.[currentYear] || 25)
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSaveChanges = async () => {
    // Validate input
    if (!staffName.trim()) {
      setError(t('validation.nameRequired'));
      return;
    }
    // ARCHIVE: PIN validation (`pin.length !== 4 || !/^\d+$/.test(pin)`) removed
    // along with the field itself.

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // ARCHIVE: both calls now go through StaffContext, which resolves
      // companyId from currentCompany (not currentUser.uid — the bug this
      // form previously had) and hashes/validates internally where relevant.

      await updateStaffMember(staffMember.id, {
        name: staffName.trim(),
        email: email.trim(),
        role
      });

      const updatedStartDate = new Date(startDateStr);
      const vacationDaysValue = parseInt(vacationDays) || 25;
      const updatedVacationDays = { ...staffMember.employmentInfo?.vacationDays };
      updatedVacationDays[currentYear] = vacationDaysValue;

      await updateStaffEmploymentInfo(staffMember.id, {
        startDate: updatedStartDate,
        vacationDays: updatedVacationDays
      });

      setSuccess(t('success.staffUpdated'));

      if (onStaffUpdated) {
        onStaffUpdated();
      }
    } catch (err) {
      console.error('Error updating staff:', err);
      setError(err.message || t('errors.failedToAddStaff'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="edit-staff-form">
      <div className="form-header">
        <h2>{t('form.editStaff')}</h2>
        <button onClick={onCancel} className="close-button">×</button>
      </div>

      <div className="tab-selector">
        <button
          className={`tab-button ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          {t('modal.basicInfo')}
        </button>
        <button
          className={`tab-button ${activeTab === 'employment' ? 'active' : ''}`}
          onClick={() => setActiveTab('employment')}
        >
          {t('modal.employment')}
        </button>
        <button
          className={`tab-button ${activeTab === 'vacation' ? 'active' : ''}`}
          onClick={() => setActiveTab('vacation')}
        >
          {t('modal.vacation')}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="form-content">
        {activeTab === 'basic' && (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="edit-staff-name">{t('form.name')}</label>
              <input
                id="edit-staff-name"
                type="text"
                value={staffName}
                onChange={(e) => setStaffName(e.target.value)}
                placeholder={t('form.staffNamePlaceholder')}
              />
            </div>

            {/* ARCHIVE: PIN form-group removed entirely */}

            <div className="form-group">
              <label htmlFor="edit-staff-email">{t('form.email')}</label>
              <input
                id="edit-staff-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('form.emailPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-staff-role">{t('form.role')}</label>
              <select
                id="edit-staff-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="staff">{t('roles.staff')}</option>
                <option value="manager">{t('roles.manager')}</option>
                <option value="admin">{t('roles.admin')}</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'employment' && (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="edit-start-date">{t('form.startDate')}</label>
              <input
                id="edit-start-date"
                type="date"
                value={startDateStr}
                onChange={(e) => setStartDateStr(e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === 'vacation' && (
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="edit-vacation-days">{t('form.annualVacationDays')} ({currentYear})</label>
              <input
                id="edit-vacation-days"
                type="number"
                value={vacationDays}
                onChange={(e) => setVacationDays(e.target.value)}
                min="0"
              />
            </div>
          </div>
        )}
      </div>

      <div className="form-actions">
        <button
          onClick={handleSaveChanges}
          disabled={isSubmitting}
          className="save-button"
        >
          {isSubmitting ? t('buttons.saving') : t('buttons.saveChanges')}
        </button>
        <button
          onClick={onCancel}
          className="cancel-button"
        >
          {t('buttons.cancel')}
        </button>
      </div>
    </div>
  );
}

export default EditStaffForm;